import { memoize } from "@use/decorators";

import { ElementModel, ElementModelData } from "@courses-pm2/api/src/ts/models/ElementModel";
import { reduceRichTextNodeStandardPageCount, renderContentNodes, RichtextContentNode } from "@courses-pm2/api/src/ts/models/elements/RichtextModel";

/**
 * Enumeration of different available targets when opening internal links on a
 * portal.
 */
export enum InternalLinkTargets {
    LIGHTBOX,
    SAME_WINDOW,
    NEW_WINDOW,
}

/**
 * Specification of data needed to instantiate a new InternalLinkModel.
 */
type InternalLinkData = {
    [K in "portalId" | "href" | "target" | "content"]: InternalLinkModel[K];
};

/**
 * Describes the content to be rendered for the element, so that it's made
 * easily available.
 */
export class InternalLinkModel extends ElementModel {
    /**
     * Specifies the id of the portal that the link belongs to (this is
     * necessary to generate the absolute url of the page, if the link is
     * rendered in another context than the portal itself).
     */
    public readonly portalId: string;

    /**
     * Specifies the path within the portal that user should be brought to, when
     * he clicks the link.
     */
    public readonly href: string;

    /**
     * Specifies the desired target, which the content of the link should be
     * opened at.
     */
    public readonly target: InternalLinkTargets;

    /**
     * Contains the content that should be rendered within the glossary word
     * trigger node.
     */
    public readonly content: RichtextContentNode[];

    /**
     * Apply data given when a new model is being instantiated.
     */
    constructor(initial: InternalLinkData & ElementModelData) {
        super(initial);

        this.portalId = initial.portalId;
        this.href = initial.href;
        this.target = initial.target;
        this.content = initial.content;
    }

    /**
     * Helper that'll transform the content of the GlossaryWord into React nodes
     * that can be mounted directly.
     */
    @memoize()
    public render(): React.ReactNode[] {
        if (!this.engine) {
            throw new Error("InternalLinkModel.render(): No rendering engine provided to this model.");
        }

        return renderContentNodes(this.content, this.engine);
    }

    /**
     * Preview is unavailable for InternalLinks.
     */
    public async getPreviewImageUrl(): Promise<undefined> {
        return undefined;
    }

    /**
     * Preview is unavailable for InternalLinks.
     */
    public getPreviewText(): string | undefined {
        return undefined;
    }

    /**
     * @inheritdoc
     *
     * Element links are able to contain RichTextNodes, so use the helper from
     * the RichtextModel to calculate standard pages for the content.
     */
    // tslint:disable-next-line: completed-docs
    @memoize()
    public getStandardPageCount(): number {
        return reduceRichTextNodeStandardPageCount(this.content);
    }
}
